import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Footer from '@/components/Footer';
import Header from '@/components/Header';

const faqData = [
  {
    category: 'Visa Services',
    items: [
      {
        q: 'Who can apply for an Indian visa at ICAC in South Korea?',
        a: 'Individuals residing in South Korea, including Korean nationals and foreign residents, can apply for Indian visas through ICAC.',
      },
      {
        q: 'What types of Indian visas are available?',
        a: 'Tourist, Business, Employment, Student, Medical, Conference, and other category-specific visas are available.',
      },
      {
        q: 'What documents are required to apply for an Indian visa?',
        a: 'Common documents include a valid passport, visa application form, recent photograph, proof of purpose (invitation, itinerary, etc.), and residency proof in Korea.',
      },
      {
        q: 'How long does it take to process a visa?',
        a: 'Processing time may vary between 3 to 7 working days, subject to approval and submission of complete documentation.',
      },
      {
        q: 'Can I track the status of my visa application?',
        a: 'Yes, you can track your application status using the reference number and Date of Birth on the ICAC or Government of India visa portal.',
      },
    ],
  },
  {
    category: 'Passport Services',
    items: [
      {
        q: 'What passport services are available at ICAC South Korea?',
        a: 'ICAC assists with reissue of passports, change in personal particulars, lost/damaged passports, and minor passport applications.',
      },
      {
        q: 'What documents are required for passport reissue?',
        a: 'A completed application form, current passport, proof of address in Korea, and other supporting documents depending on the service requested.',
      },
      {
        q: 'Is police verification required for passport services?',
        a: 'Yes, for certain cases such as first-time issue or change in particulars, verification may be required.',
      },
      {
        q: 'How long does it take to receive a reissued passport?',
        a: 'On average, 4 to 6 weeks, depending on clearance from authorities in India.',
      },
      {
        q: 'Can I apply for passport services without visiting the ICAC physically?',
        a: 'No, the applicant must appear in person for biometric capture and verification.',
      },
    ],
  },
  {
    category: 'OCI Services',
    items: [
      {
        q: 'Who is eligible for an OCI card?',
        a: 'Persons of Indian origin or their descendants up to four generations, except those who have ever been citizens of Pakistan or Bangladesh.',
      },
      {
        q: 'What documents are required for an OCI application?',
        a: 'Indian origin proof, current passport, photographs, and supporting documents for the relationship if applying as a descendant.',
      },
      {
        q: 'How long does the OCI process take?',
        a: 'Typically, 6–8 weeks after complete submission, subject to clearance from the Indian authorities.',
      },
      {
        q: 'Can I apply for a new OCI if my passport has changed?',
        a: 'Yes, in such cases, you must update your OCI details by applying for OCI Miscellaneous Services.',
      },
      {
        q: 'Can minors apply for an OCI card?',
        a: 'Yes, minors of Indian origin are eligible. The application must be submitted by parents or legal guardians.',
      },
    ],
  },
  {
    category: 'Consular Services',
    items: [
      {
        q: 'What types of consular services does ICAC provide?',
        a: 'ICAC facilitates attestation of documents, life certificates, affidavits, NRI certificates, PCCs, and more.',
      },
      {
        q: 'Is prior appointment required for consular services?',
        a: 'Yes, all applicants must book appointments in advance to submit consular service requests.',
      },
      {
        q: 'What is the process for document attestation?',
        a: 'Documents must be apostilled (if applicable), translated into English, and submitted with identification proof and a request letter.',
      },
      {
        q: 'Can I obtain a Police Clearance Certificate (PCC) from ICAC?',
        a: 'Yes, ICAC processes PCC requests for Indian and Korean nationals based on valid purpose and documentation.',
      },
      {
        q: 'Is online payment available for ICAC applications?',
        a: 'For postal applications, payments are accepted via bank transfer. For walk-in applications at the ICAC centre, payments can be made in cash.',
      },
    ],
  },
];

const AccordionItem = ({ question, answer, index, isOpen, onToggle }) => (
  <div className={`faq-item ${isOpen ? 'open' : ''}`} onClick={onToggle}>
    <div className="faq-question">
      <span className="faq-q-num">{String(index).padStart(2, '0')}</span>
      <span className="faq-q-text">{question}</span>
      <ChevronDown size={16} strokeWidth={2} className="faq-chevron" />
    </div>
    <div className="faq-answer">
      <p>{answer}</p>
    </div>
  </div>
);

const FAQs = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);

  const handleTabChange = (i) => {
    setActiveTab(i);
    setOpenIndex(null);
  };

  const handleToggle = (i) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .faq-page {
          font-family: 'DM Sans', sans-serif;
          background: #f4f7f9;
          min-height: 100vh;
          color: #1a2e3d;
        }

        /* ── Hero ── */
        .faq-hero {
        //   background: #0f1f2e;
        background: #162b56;
          padding: 56px 32px 48px;
          border-bottom: 2px solid #c9a84c;
          text-align: center;
        }

        .faq-eyebrow {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 14px;
        }

        .faq-hero h1 {
          font-family: 'Lora', serif;
          font-size: 36px;
          font-weight: 500;
          color: #d9e8f0;
          margin-bottom: 12px;
          line-height: 1.25;
        }

        .faq-hero p {
          font-size: 14px;
          font-weight: 300;
          color: #7a98a8;
          line-height: 1.75;
        }

        /* ── Body ── */
        .faq-body {
          max-width: 820px;
          margin: 0 auto;
          padding: 48px 32px 72px;
        }

        /* ── Tabs ── */
        .faq-tabs {
          display: flex;
          gap: 6px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }

        .faq-tab {
          padding: 8px 18px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #546e7a;
          background: #fff;
          border: 1px solid rgba(15,31,46,0.1);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .faq-tab:hover {
          border-color: rgba(201,168,76,0.3);
          color: #c9a84c;
        }

        .faq-tab.active {
          background: #0f1f2e;
          border-color: #0f1f2e;
          color: #e8c96d;
        }

        /* ── Section Label ── */
        .faq-section-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 16px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(201,168,76,0.2);
        }

        /* ── Accordion ── */
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .faq-item {
          background: #fff;
          border: 1px solid rgba(15,31,46,0.08);
          border-radius: 10px;
          cursor: pointer;
          overflow: hidden;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .faq-item:hover {
          border-color: rgba(201,168,76,0.25);
        }

        .faq-item.open {
          border-color: rgba(201,168,76,0.35);
          box-shadow: 0 4px 16px rgba(15,31,46,0.06);
        }

        .faq-question {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
        }

        .faq-q-num {
          font-family: 'Lora', serif;
          font-size: 13px;
          font-weight: 500;
          color: #c9a84c;
          flex-shrink: 0;
          min-width: 24px;
        }

        .faq-q-text {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: #1a2e3d;
          line-height: 1.45;
        }

        .faq-chevron {
          flex-shrink: 0;
          color: #a0b8c4;
          transition: transform 0.25s ease, color 0.2s;
        }

        .faq-item.open .faq-chevron {
          transform: rotate(180deg);
          color: #c9a84c;
        }

        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s ease;
          padding: 0 20px 0 58px;
        }

        .faq-item.open .faq-answer {
          max-height: 200px;
          padding: 0 20px 18px 58px;
        }

        .faq-answer p {
          font-size: 13.5px;
          font-weight: 400;
          color: #4a6475;
          line-height: 1.75;
          border-top: 1px solid rgba(15,31,46,0.06);
          padding-top: 14px;
        }

        /* ── Help note ── */
        .faq-note {
          margin-top: 32px;
          background: #fff;
          border: 1px solid rgba(201,168,76,0.2);
          border-left: 3px solid #c9a84c;
          border-radius: 8px;
          padding: 16px 20px;
        }

        .faq-note-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 4px;
        }

        .faq-note-text {
          font-size: 13px;
          color: #4a6475;
          line-height: 1.7;
        }

        .faq-note-text a {
          color: #c9a84c;
          text-decoration: none;
          font-weight: 500;
        }

        @media (max-width: 600px) {
          .faq-hero { padding: 40px 20px 36px; }
          .faq-hero h1 { font-size: 28px; }
          .faq-body { padding: 32px 16px 52px; }
          .faq-answer { padding: 0 16px 0 16px; }
          .faq-item.open .faq-answer { padding: 0 16px 16px 16px; }
          .faq-question { padding: 16px; gap: 10px; }
        }
      `}</style>

      <Header />

      <div className="faq-page">

        {/* Hero */}
        <div className="faq-hero">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common queries about our visa, passport, OCI, and consular services.</p>
        </div>

        {/* Body */}
        <div className="faq-body">

          {/* Tabs */}
          <div className="faq-tabs">
            {faqData.map((cat, i) => (
              <button
                key={cat.category}
                className={`faq-tab ${activeTab === i ? 'active' : ''}`}
                onClick={() => handleTabChange(i)}
              >
                {cat.category}
              </button>
            ))}
          </div>

          {/* Active category */}
          <p className="faq-section-label">{faqData[activeTab].category}</p>

          <div className="faq-list">
            {faqData[activeTab].items.map((item, i) => (
              <AccordionItem
                key={i}
                index={activeTab * 5 + i + 1}
                question={item.q}
                answer={item.a}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}
          </div>
       </div>
      </div>

      <Footer />
    </>
  );
};

export default FAQs;